import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import HRDashboardTemplate, { HR_DASHBOARD_TABS, viewFromTab } from "Components/templates/hr/hr-dashboard";
import ActionButton from "Components/atoms/buttons/action-button";
import SelectInput from "Components/molecule/inputs/select-input";
import SyncButton from "Components/molecule/sync-button";
import AreYouSureModal from "Components/organisms/are-you-sure-modal";
import { HrExportFormat, HrGroupBy, HrView } from "models/hr-dashboard";
import Screen, { SCREENS } from "models/screen";
import { SyncLogTypes } from "models/sync-log";
import FiscalCalendar from "utils/fiscal-calendar";
import { PermissionsSliceState } from "slices/auth/permissions/reducer";
import { HumanResourcesSliceState, setShowSyncModal } from "slices/human-resources/reducer";
import { syncDayforceData } from "slices/human-resources/thunk";
import useSyncProcess from "hooks/useSyncProcess";
import { SyncLogSliceState } from "slices/sync-log/reducer";
import { getMostRecentSyncLog } from "slices/thunk";
import {
    HrDashboardSliceState,
    resetHrViews,
    setHrDashboardLoading,
    setHrFiscalYear,
    setHrGroupBy,
    setHrView,
} from "slices/human-resources/hr-dashboard/reducer";
import { downloadHrDashboardExport, getHrView } from "slices/human-resources/hr-dashboard/thunk";

const selectHrDashboard = createSelector(
    (state: any) => state.HrDashboard,
    ({ fiscalYear, groupBy, views, loadedFor, isLoading }: HrDashboardSliceState) => ({ fiscalYear, groupBy, views, loadedFor, isLoading })
);

const selectPermissions = createSelector(
    (state: any) => state.Permissions,
    ({ screens }: PermissionsSliceState) => ({ screens })
);

const selectHumanResources = createSelector(
    (state: any) => state.HumanResources,
    ({ showSyncModal, isSyncingData }: HumanResourcesSliceState) => ({ showSyncModal, isSyncingData })
);

const selectSyncLog = createSelector(
    (state: any) => state.SyncLog,
    ({ syncLogs }: SyncLogSliceState) => ({ syncLogs })
);

// Show the current FY and the 4 before it
const FISCAL_YEARS_BACK = 4;

// Views that depend on the department / division toggle
const GROUPED_VIEWS: HrView[] = ["by-group", "retention"];
const loadKey = (view: HrView, fy: number, groupBy: HrGroupBy) => (GROUPED_VIEWS.includes(view) ? `${fy}|${groupBy}` : `${fy}`);

const HRDashboardPage = () => {
    document.title = "HR Dashboard | IbexIQ";

    const dispatch: any = useDispatch();
    const navigate = useNavigate();
    const { fiscalYear, groupBy, views, loadedFor, isLoading } = useSelector(selectHrDashboard);
    const { screens } = useSelector(selectPermissions);
    const { showSyncModal } = useSelector(selectHumanResources);
    const { isSyncing: isSyncingData, startSync } = useSyncProcess({
        module: SyncLogTypes.EMPLOYEE_DAYFORCE,
        start: syncDayforceData,
        onFinished: () => { dispatch(resetHrViews()) },
    });
    const { syncLogs } = useSelector(selectSyncLog);

    const [activeTab, setActiveTab] = useState<string>(HR_DASHBOARD_TABS.headcount);
    const [isExporting, setIsExporting] = useState(false);

    const hasScreen = (code: SCREENS) => screens.some((s: Screen) => s.code === code);
    const isHrAdmin = useMemo(() => hasScreen(SCREENS.HR_ADMIN), [screens]);
    // The Dayforce sync endpoint is guarded by the Employees screen
    const canSync = useMemo(() => hasScreen(SCREENS.HR_EMPLOYEES), [screens]);

    const fiscalYearOptions = useMemo(() => {
        const current = FiscalCalendar.currentFiscalYear();
        return Array.from({ length: FISCAL_YEARS_BACK + 1 }, (_, i) => current - i)
            .map(fy => ({ value: String(fy), label: `FY${fy}` }));
    }, []);

    const activeView = viewFromTab(activeTab);
    const inFlight = useRef<string | null>(null);

    // Settings may have changed since the last visit, so start fresh on every visit
    useEffect(() => {
        dispatch(resetHrViews());
    }, []);

    // Load the active tab when it has no data for the current FY (and grouping)
    useEffect(() => {
        const key = loadKey(activeView, fiscalYear, groupBy);
        if (loadedFor[activeView] === key || inFlight.current === `${activeView}|${key}`) return;
        inFlight.current = `${activeView}|${key}`;

        const load = async () => {
            dispatch(setHrDashboardLoading(true));
            const data = await getHrView(activeView, fiscalYear, groupBy);
            dispatch(setHrView({ view: activeView, key, data }));
            dispatch(setHrDashboardLoading(false));
            inFlight.current = null;
        };
        load();
    }, [activeView, fiscalYear, groupBy, loadedFor]);

    useEffect(() => {
        if (canSync && !syncLogs[SyncLogTypes.EMPLOYEE_DAYFORCE])
            dispatch(getMostRecentSyncLog(SyncLogTypes.EMPLOYEE_DAYFORCE));
    }, [canSync]);

    const handleExport = async (format: HrExportFormat) => {
        setIsExporting(true);
        await downloadHrDashboardExport(activeView, fiscalYear, groupBy, format);
        setIsExporting(false);
    };

    const handleSyncData = async () => {
        dispatch(setShowSyncModal({ show: false }));
        await startSync();
    };

    const actions = <>
        <SelectInput
            options={fiscalYearOptions}
            value={String(fiscalYear)}
            onChange={e => dispatch(setHrFiscalYear(Number(e.target.value)))}
        />
        <ActionButton onClick={() => handleExport("xlsx")} disabled={isExporting || isLoading}>
            Excel <i className="bx bx-download" />
        </ActionButton>
        <ActionButton onClick={() => handleExport("csv")} disabled={isExporting || isLoading}>
            CSV <i className="bx bx-download" />
        </ActionButton>
        {/* Full report of every tab, in a new browser tab */}
        <ActionButton onClick={() => window.open(`/human-resources/dashboard/print?fy=${fiscalYear}&groupBy=${groupBy}`, "_blank")}>
            Print <i className="bx bx-printer" />
        </ActionButton>
        {canSync && (
            <SyncButton
                id="hrDashboardSyncBtn"
                isSyncing={isSyncingData}
                syncLog={syncLogs[SyncLogTypes.EMPLOYEE_DAYFORCE]}
                onClick={() => dispatch(setShowSyncModal({ show: true }))}
            />
        )}
        {isHrAdmin && (
            <ActionButton onClick={() => navigate("/human-resources/dashboard/settings")}>
                Settings <i className="bx bx-cog" />
            </ActionButton>
        )}
    </>;

    return <HRDashboardTemplate
        views={views}
        groupBy={groupBy}
        setGroupBy={(value: HrGroupBy) => dispatch(setHrGroupBy(value))}
        isLoading={isLoading}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        actions={actions}
        modal={<AreYouSureModal
            isOpen={showSyncModal}
            onModalClose={() => dispatch(setShowSyncModal({ show: false }))}
            modalTitle={"Are you sure you want to sync?"}
            handleAreYouSureAccept={handleSyncData}
            handleAreYouSureDecline={() => dispatch(setShowSyncModal({ show: false }))}
            areYouSureacceptText={isSyncingData ? "Syncing data...." : "Sync Data"}
            areYouSuredeclineText={"Cancel"} />}
    />;
};

export default HRDashboardPage;
